'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  prenom: string;
  nom: string;
  email: string;
  telephone: string;
  conciergerie: string;
  nbLogements: string;
  villes: string;
  plateformes: string;
  motivation: string;
}

interface FormErrors {
  prenom?: string;
  nom?: string;
  email?: string;
  telephone?: string;
  conciergerie?: string;
  nbLogements?: string;
  villes?: string;
  motivation?: string;
}

const initialForm: FormData = {
  prenom: '',
  nom: '',
  email: '',
  telephone: '',
  conciergerie: '',
  nbLogements: '',
  villes: '',
  plateformes: '',
  motivation: '',
};

export default function CandidatureForm() {
  const [form, setForm] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  // Validation functions
  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) return 'L\'email est requis';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Format d\'email invalide';
    return undefined;
  };

  const validatePhone = (phone: string): string | undefined => {
    if (!phone.trim()) return undefined; // Optional field
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
    if (!phoneRegex.test(phone.replace(/\s/g, ''))) return 'Format de téléphone invalide';
    return undefined;
  };

  const validateMotivation = (text: string): string | undefined => {
    if (!text.trim()) return 'La motivation est requise';
    const wordCount = text.trim().split(/\s+/).length;
    if (wordCount < 50) return `Minimum 50 mots requis (actuellement ${wordCount} mots)`;
    return undefined;
  };

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'prenom':
        return !value.trim() ? 'Le prénom est requis' : value.trim().length < 2 ? 'Le prénom doit contenir au moins 2 caractères' : undefined;
      case 'nom':
        return !value.trim() ? 'Le nom est requis' : value.trim().length < 2 ? 'Le nom doit contenir au moins 2 caractères' : undefined;
      case 'email':
        return validateEmail(value);
      case 'telephone':
        return validatePhone(value);
      case 'conciergerie':
        return !value.trim() ? 'Le nom de la conciergerie est requis' : value.trim().length < 3 ? 'Le nom doit contenir au moins 3 caractères' : undefined;
      case 'nbLogements':
        return !value ? 'Le nombre de logements est requis' : undefined;
      case 'villes':
        return !value.trim() ? 'Les villes d\'opération sont requises' : undefined;
      case 'motivation':
        return validateMotivation(value);
      default:
        return undefined;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    
    // Validate on change if field was touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    // Validate all required fields
    Object.keys(form).forEach((key) => {
      if (key !== 'telephone' && key !== 'plateformes') { // Skip optional fields
        const error = validateField(key, form[key as keyof FormData]);
        if (error) {
          newErrors[key as keyof FormErrors] = error;
          isValid = false;
        }
      }
    });

    // Validate optional phone if provided
    if (form.telephone) {
      const phoneError = validatePhone(form.telephone);
      if (phoneError) {
        newErrors.telephone = phoneError;
        isValid = false;
      }
    }

    setErrors(newErrors);
    
    // Mark all fields as touched
    const allTouched: Record<string, boolean> = {};
    Object.keys(form).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate entire form
    if (!validateForm()) {
      return;
    }

    setStatus('submitting');

    // Construct mailto body
    const body = `
Candidature Programme Partenaire GiteZen

Prénom : ${form.prenom}
Nom : ${form.nom}
Email : ${form.email}
Téléphone : ${form.telephone}
Conciergerie : ${form.conciergerie}
Nombre de logements : ${form.nbLogements}
Villes d'opération : ${form.villes}
Plateformes utilisées : ${form.plateformes}

Motivation :
${form.motivation}
    `.trim();

    const subject = `Candidature Programme Partenaire GiteZen - ${form.conciergerie}`;
    const mailtoLink = `mailto:contact@full-loc.fr?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    try {
      // Open mailto link - works with all email clients
      window.open(mailtoLink, '_self');
      setTimeout(() => setStatus('success'), 1000);
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="glass-card rounded-2xl p-12 text-center">
        <div className="w-20 h-20 rounded-full bg-moss/20 border-2 border-moss/40 flex items-center justify-center mx-auto mb-6 animate-pulse">
          <Icon name="CheckIcon" size={40} className="text-moss-light" />
        </div>
        <h2 className="font-fraunces text-4xl italic text-cream mb-4">Candidature envoyée avec succès !</h2>
        <div className="max-w-2xl mx-auto space-y-4 mb-8">
          <p className="text-cream/80 leading-relaxed text-lg">
            Votre client email s'est ouvert avec votre candidature pré-remplie.
          </p>
          <div className="glass-card rounded-xl p-6 border border-moss/30">
            <div className="flex items-start gap-3">
              <Icon name="InformationCircleIcon" size={24} className="text-moss-light flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <p className="text-cream/90 font-semibold mb-2">Prochaines étapes :</p>
                <ol className="text-cream/70 text-sm space-y-2 list-decimal list-inside">
                  <li>Vérifiez les informations dans votre client email</li>
                  <li>Cliquez sur "Envoyer" pour finaliser votre candidature</li>
                  <li>Notre équipe vous répondra sous <strong className="text-moss-light">48h ouvrées</strong></li>
                </ol>
              </div>
            </div>
          </div>
          <p className="text-cream/60 text-sm">
            Candidature envoyée à <span className="text-moss-light font-semibold">contact@full-loc.fr</span>
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="/programme-partenaire"
            className="inline-flex items-center gap-2 bg-moss text-cream px-8 py-3 rounded-full font-semibold hover:bg-moss-light transition-all shadow-lg shadow-moss/25"
          >
            <Icon name="ArrowLeftIcon" size={18} />
            Retour au programme
          </a>
          <button
            onClick={() => {
              setStatus('idle');
              setForm(initialForm);
              setErrors({});
              setTouched({});
            }}
            className="inline-flex items-center gap-2 border border-white/10 text-cream/60 px-8 py-3 rounded-full font-medium hover:text-cream hover:border-white/20 transition-all"
          >
            <Icon name="PencilIcon" size={18} />
            Nouvelle candidature
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-8 md:p-10 space-y-6">
      {/* Identity */}
      <div>
        <h3 className="text-cream/40 text-xs font-bold uppercase tracking-widest mb-5 flex items-center gap-2">
          <span className="w-5 h-px bg-moss/40" />
          Vos informations
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Prénom *</label>
            <input
              type="text"
              name="prenom"
              value={form.prenom}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Camille"
              className={`form-input ${touched.prenom && errors.prenom ? 'border-red-500/50 focus:border-red-500' : ''}`}
            />
            {touched.prenom && errors.prenom && (
              <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={14} />
                {errors.prenom}
              </p>
            )}
          </div>
          <div>
            <label className="form-label">Nom *</label>
            <input
              type="text"
              name="nom"
              value={form.nom}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Renard"
              className={`form-input ${touched.nom && errors.nom ? 'border-red-500/50 focus:border-red-500' : ''}`}
            />
            {touched.nom && errors.nom && (
              <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={14} />
                {errors.nom}
              </p>
            )}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="form-label">Email professionnel *</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="camille@ma-conciergerie.fr"
              className={`form-input ${touched.email && errors.email ? 'border-red-500/50 focus:border-red-500' : ''}`}
            />
            {touched.email && errors.email && (
              <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={14} />
                {errors.email}
              </p>
            )}
          </div>
          <div>
            <label className="form-label">Téléphone</label>
            <input
              type="tel"
              name="telephone"
              value={form.telephone}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="+33 6 12 34 56 78"
              className={`form-input ${touched.telephone && errors.telephone ? 'border-red-500/50 focus:border-red-500' : ''}`}
            />
            {touched.telephone && errors.telephone && (
              <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={14} />
                {errors.telephone}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Business */}
      <div>
        <h3 className="text-cream/40 text-xs font-bold uppercase tracking-widest mb-5 flex items-center gap-2">
          <span className="w-5 h-px bg-moss/40" />
          Votre activité
        </h3>
        <div className="space-y-4">
          <div>
            <label className="form-label">Nom de votre conciergerie / structure *</label>
            <input
              type="text"
              name="conciergerie"
              value={form.conciergerie}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Conciergerie Azur"
              className={`form-input ${touched.conciergerie && errors.conciergerie ? 'border-red-500/50 focus:border-red-500' : ''}`}
            />
            {touched.conciergerie && errors.conciergerie && (
              <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={14} />
                {errors.conciergerie}
              </p>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Nombre de logements gérés *</label>
              <select
                name="nbLogements"
                value={form.nbLogements}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`form-input ${touched.nbLogements && errors.nbLogements ? 'border-red-500/50 focus:border-red-500' : ''}`}
              >
                <option value="" disabled>Sélectionner...</option>
                <option value="5-10">5 à 10 logements</option>
                <option value="11-25">11 à 25 logements</option>
                <option value="26-50">26 à 50 logements</option>
                <option value="51-100">51 à 100 logements</option>
                <option value="100+">Plus de 100 logements</option>
              </select>
              {touched.nbLogements && errors.nbLogements && (
                <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={14} />
                  {errors.nbLogements}
                </p>
              )}
            </div>
            <div>
              <label className="form-label">Villes d'opération *</label>
              <input
                type="text"
                name="villes"
                value={form.villes}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Nice, Cannes, Antibes"
                className={`form-input ${touched.villes && errors.villes ? 'border-red-500/50 focus:border-red-500' : ''}`}
              />
              {touched.villes && errors.villes && (
                <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={14} />
                  {errors.villes}
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="form-label">Plateformes utilisées actuellement</label>
            <input
              type="text"
              name="plateformes"
              value={form.plateformes}
              onChange={handleChange}
              placeholder="Airbnb, Booking.com, Abritel..."
              className="form-input"
            />
          </div>
        </div>
      </div>

      {/* Motivation */}
      <div>
        <h3 className="text-cream/40 text-xs font-bold uppercase tracking-widest mb-5 flex items-center gap-2">
          <span className="w-5 h-px bg-moss/40" />
          Votre motivation
        </h3>
        <div>
          <label className="form-label">Pourquoi souhaitez-vous rejoindre le programme ? *</label>
          <textarea
            name="motivation"
            value={form.motivation}
            onChange={handleChange}
            onBlur={handleBlur}
            rows={5}
            placeholder="Décrivez votre situation actuelle (outils utilisés, problèmes rencontrés) et ce que vous attendez du programme GiteZen..."
            className={`form-input resize-none ${touched.motivation && errors.motivation ? 'border-red-500/50 focus:border-red-500' : ''}`}
          />
          {touched.motivation && errors.motivation && (
            <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              {errors.motivation}
            </p>
          )}
          <p className="text-cream/30 text-xs mt-2">
            Minimum 50 mots. Les candidatures détaillées sont prioritaires.
            {form.motivation && (
              <span className={`ml-2 ${form.motivation.trim().split(/\s+/).length >= 50 ? 'text-moss-light' : 'text-cream/40'}`}>
                ({form.motivation.trim().split(/\s+/).filter(w => w.length > 0).length} mots)
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Engagement */}
      <div className="glass-card rounded-xl p-5 border border-moss/20">
        <p className="text-cream/60 text-xs leading-relaxed">
          En soumettant ce formulaire, vous confirmez gérer <strong className="text-cream">au minimum 5 logements</strong> en location saisonnière et vous engagez à utiliser GiteZen réellement pendant 3 mois, à partager votre retour d'expérience et à publier un témoignage. Aucun engagement financier n'est requis.
        </p>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full flex items-center justify-center gap-3 bg-moss text-cream py-4 rounded-full font-bold text-base hover:bg-moss-light transition-all duration-300 shadow-xl shadow-moss/25 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === 'submitting' ? (
          <>
            <div className="w-5 h-5 rounded-full border-2 border-cream/30 border-t-cream animate-spin" />
            Préparation de l'envoi...
          </>
        ) : (
          <>
            <Icon name="PaperAirplaneIcon" size={18} />
            Envoyer ma candidature
          </>
        )}
      </button>

      <p className="text-center text-cream/30 text-xs">
        Votre candidature sera envoyée à{' '}
        <span className="text-moss-light">contact@full-loc.fr</span>
        {' '}— Réponse sous 48h ouvrées
      </p>
    </form>
  );
}